import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/contexts/LangContext";

function formatErr(detail) {
    if (!detail) return "";
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail))
        return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
    return String(detail);
}

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function googleLogin(role) {
    localStorage.setItem("flow_pending_role", role);
    const redirectUrl = window.location.origin + "/auth/callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

export default function Login() {
    const { t } = useLang();
    const { login } = useAuth();
    const nav = useNavigate();
    const loc = useLocation();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [role, setRole] = useState("student");
    const [err, setErr] = useState("");
    const [loading, setLoading] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setErr("");
        setLoading(true);
        try {
            const u = await login(email, password);
            nav("/dashboard", { replace: true });
        } catch (e) {
            setErr(formatErr(e.response?.data?.detail) || t.auth.bad);
        } finally {
            setLoading(false);
        }
    };

    return (
        <main
            className="min-h-[80vh] flex items-center justify-center px-5"
            data-testid="login-page"
        >
            <div className="w-full max-w-md card p-8">
                <h1 className="font-display text-3xl text-white uppercase">
                    {t.auth.loginHead}
                </h1>
                <p className="font-brush text-[var(--lime)] text-3xl -rotate-2 inline-block mt-1">
                    welcome back
                </p>

                <form onSubmit={submit} className="mt-6 space-y-4">
                    <label className="block">
                        <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-[var(--muted)]">
                            {t.auth.email}
                        </span>
                        <input
                            type="email"
                            required
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            className="input-line mt-1"
                            data-testid="login-email"
                        />
                    </label>
                    <label className="block">
                        <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-[var(--muted)]">
                            {t.auth.password}
                        </span>
                        <input
                            type="password"
                            required
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            className="input-line mt-1"
                            data-testid="login-password"
                        />
                    </label>
                    {err && (
                        <p
                            className="font-mono text-sm text-[var(--coral)]"
                            data-testid="login-error"
                        >
                            {err}
                        </p>
                    )}
                    <button
                        type="submit"
                        disabled={loading}
                        className="btn-prime w-full justify-center"
                        data-testid="login-submit"
                    >
                        {loading && <Loader2 size={14} className="animate-spin" />}
                        {t.auth.submitLogin}
                    </button>
                </form>

                <div className="flex items-center gap-3 my-6">
                    <span className="flex-1 h-px bg-[var(--border)]" />
                    <span className="font-mono text-xs text-[var(--muted)] uppercase tracking-widest">
                        {t.auth.or}
                    </span>
                    <span className="flex-1 h-px bg-[var(--border)]" />
                </div>

                <div className="space-y-3">
                    <label className="flex items-center gap-3 font-mono text-xs uppercase tracking-[0.18em] text-[var(--muted)]">
                        {t.auth.role}:
                        <select
                            value={role}
                            onChange={(e) => setRole(e.target.value)}
                            className="bg-black border border-[var(--border)] text-white p-1 font-mono"
                            data-testid="login-google-role"
                        >
                            <option value="student">{t.auth.student}</option>
                            <option value="teacher">{t.auth.teacher}</option>
                        </select>
                    </label>
                    <button
                        type="button"
                        onClick={() => googleLogin(role)}
                        className="btn-ghost w-full justify-center"
                        data-testid="login-google"
                    >
                        <span className="text-[var(--lime)] mr-2">G</span>
                        {t.auth.google}
                    </button>
                </div>

                <Link
                    to="/signup"
                    state={loc.state}
                    className="lnk mt-6 inline-block font-mono text-sm"
                    data-testid="switch-signup"
                >
                    {t.auth.switchToSignup}
                </Link>
            </div>
        </main>
    );
}
